import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  getGalleryPackages,
  getGalleryPhotos,
  getGalleryItemPhotos,
  galleryThumbUrl,
  downloadPhotos,
} from "api/gallery";
import AuthenticatedImage from "components/AuthenticatedImage";
import {
  MONO,
  TEXT_DARK,
  TEXT_SECONDARY,
  TEXT_SUBTLE,
  BORDER,
  BG_WHITE,
  BG_TINT,
  CARD_SHADOW,
  RADIUS_TILE,
} from "components/bento/bentoTokens";
import FilterBar from "./FilterBar";
import PhotoLightbox from "./PhotoLightbox";
import TrackingDetailDialog from "./TrackingDetailDialog";
import { DayHeader, PhotoTile, PhotoListRow, TileSkeleton, RowSkeleton, EmptyHint, groupByDay, fmtTime, statusChipColor } from "./parts";

const GRID_SX = {
  display: "grid",
  gap: "12px",
  gridTemplateColumns: "repeat(auto-fill, minmax(158px, 1fr))",
};

const CARD_GRID_SX = {
  display: "grid",
  gap: "12px",
  gridTemplateColumns: "repeat(auto-fill, minmax(196px, 1fr))",
};

const LIST_SX = { display: "flex", flexDirection: "column", gap: "8px" };

const DEFAULT_FILTERS = { q: "", customer: null, preset: "all", from: "", to: "", category: "" };

// One package = one card: up to 4 stacked thumbnails + tracking + customer + counts.
const PackageCard = ({ row, onOpen, t }) => {
  const previews = row.previewPhotos || [];
  const extra = row.photoCount - previews.length;
  return (
    <ButtonBase
      onClick={onOpen}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 1,
        p: "10px",
        borderRadius: RADIUS_TILE,
        border: `1px solid ${BORDER}`,
        backgroundColor: BG_WHITE,
        textAlign: "left",
        transition: "transform .12s ease, box-shadow .12s ease",
        "&:hover": { transform: "translateY(-2px)", boxShadow: CARD_SHADOW },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: previews.length > 1 ? "1fr 1fr" : "1fr",
          gap: "4px",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        {previews.map((p, i) => (
          <Box key={i} sx={{ position: "relative", aspectRatio: previews.length > 1 ? "1.35" : "2.4", backgroundColor: BG_TINT }}>
            <AuthenticatedImage
              src={galleryThumbUrl("packages", p)}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
            {/* A package's photos can span stations as it moves, so this is tagged
                per-thumbnail (station code, not full name — the cell is small). */}
            {p.station && (
              <Box
                sx={{
                  position: "absolute",
                  top: 4,
                  left: 4,
                  px: "5px",
                  py: "1px",
                  borderRadius: "5px",
                  fontSize: 8.5,
                  fontWeight: 700,
                  color: "#fff",
                  backgroundColor: "rgba(109,40,217,.72)",
                  maxWidth: "80%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {p.station}
              </Box>
            )}
            {i === previews.length - 1 && extra > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(16,14,28,.55)",
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: 15,
                }}
              >
                +{extra}
              </Box>
            )}
          </Box>
        ))}
      </Box>
      <Box sx={{ px: 0.4, pb: 0.4 }}>
        <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 13, color: TEXT_DARK }}>{row.tracking}</Typography>
        <Typography noWrap sx={{ fontSize: 12, color: TEXT_SECONDARY }}>
          {row.customerName || "—"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mt: 0.6 }}>
          <Chip size="small" label={t("gallery.photoCount", { count: row.photoCount })} color="secondary" variant="outlined" sx={{ height: 20, fontSize: 10.5 }} />
          <Chip size="small" label={row.status} color={statusChipColor(row.status)} variant="outlined" sx={{ height: 20, fontSize: 10.5 }} />
          <Typography sx={{ ml: "auto", fontSize: 10.5, color: TEXT_SUBTLE, fontVariantNumeric: "tabular-nums" }}>
            {fmtTime(row.latestPhotoAt)}
          </Typography>
        </Box>
      </Box>
    </ButtonBase>
  );
};

// Right-side drawer: every photo of one package + download-all.
const PackageDrawer = ({ open, onClose, packageId, t, i18n, onOpenLightbox, onOpenTracking }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !packageId) return;
    setDetail(null);
    setLoading(true);
    getGalleryItemPhotos("packages", packageId).then((res) => {
      if (res.success) setDetail(res.data);
      else toast.error(res.message);
      setLoading(false);
    });
  }, [open, packageId]);

  const downloadAll = async () => {
    setSaving(true);
    const saved = await downloadPhotos("packages", detail.photos, detail.tracking);
    setSaving(false);
    if (saved > 0) toast.success(t("gallery.savedPhotos", { count: saved }));
    else toast.error(t("gallery.downloadFailed"));
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "94vw", sm: 560 } } }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: "18px 22px 14px", borderBottom: `1px solid ${BORDER}` }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <Typography
              onClick={() => detail && onOpenTracking(detail.tracking)}
              title={t("gallery.trackingDetail")}
              sx={{
                fontFamily: MONO,
                fontWeight: 800,
                fontSize: 17,
                color: TEXT_DARK,
                cursor: detail ? "pointer" : "default",
                "&:hover": detail ? { color: "secondary.main", textDecoration: "underline" } : undefined,
              }}
            >
              {detail?.tracking || "…"}
            </Typography>
            {detail && (
              <IconButton
                size="small"
                aria-label={t("gallery.copyTracking")}
                onClick={() => {
                  navigator.clipboard?.writeText(detail.tracking);
                  toast.success(t("gallery.trackingCopied"));
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 15 }} />
              </IconButton>
            )}
          </Box>
          <Typography sx={{ fontSize: 12.5, color: TEXT_SECONDARY }}>
            {detail ? `${detail.customerName || "—"}` : ""}
          </Typography>
        </Box>
        {detail && <Chip size="small" label={detail.status} color={statusChipColor(detail.status)} />}
        <IconButton onClick={onClose} aria-label={t("common.close")}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", p: "18px 22px 26px" }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress color="secondary" size={28} />
          </Box>
        )}
        {detail && (
          <>
            <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: TEXT_SUBTLE, mb: 1.2 }}>
              {t("gallery.allPhotosOfPackage", { count: detail.photos.length })}
            </Typography>
            <Box sx={{ display: "grid", gap: "10px", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))" }}>
              {detail.photos.map((p, i) => (
                <PhotoTile
                  key={i}
                  album="packages"
                  photo={p}
                  badge={p.category ? t(`gallery.category.${p.category}`) : null}
                  stationBadge={p.stationName || null}
                  subtitle={fmtTime(p.timestamp)}
                  onClick={() => onOpenLightbox(detail, i)}
                />
              ))}
            </Box>
            <Box sx={{ display: "flex", gap: 1.2, mt: 3, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                disabled={saving || detail.photos.length === 0}
                onClick={downloadAll}
              >
                {detail.photos.length > 1
                  ? t("gallery.downloadAll", { count: detail.photos.length })
                  : t("gallery.downloadPhoto")}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

const PackagesTab = () => {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [grouped, setGrouped] = useState(true);
  // Tile/List only applies to the flat (ungrouped) photo view — grouped package
  // cards are a distinct layout, not a per-photo one.
  const [viewMode, setViewMode] = useState("tile");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [drawerPkg, setDrawerPkg] = useState(null); // packageId
  const [lightbox, setLightbox] = useState(null); // { photos, index, itemPhotos, baseName }
  const [trackingDialog, setTrackingDialog] = useState(null); // tracking number shown in the detail modal
  const debounceRef = useRef();

  const load = useCallback(
    async (pageNum, append) => {
      setLoading(true);
      const params = {
        from: filters.from,
        to: filters.to,
        q: filters.q,
        category: filters.category,
        customerId: filters.customer?._id || "",
        page: pageNum,
      };
      const res = grouped
        ? await getGalleryPackages({ ...params, limit: 24 })
        : await getGalleryPhotos({ ...params, album: "packages", limit: 48 });
      if (res.success) {
        setRows((prev) => (append ? [...prev, ...res.data] : res.data));
        setTotal(res.total);
        setPage(pageNum);
      } else {
        toast.error(res.message);
      }
      setLoading(false);
    },
    [filters, grouped]
  );

  // Debounced reload on filter change (text inputs fire per keystroke)
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(1, false), 300);
    return () => clearTimeout(debounceRef.current);
  }, [load]);

  const openPkgLightbox = (detail, index) => {
    setLightbox({
      photos: detail.photos.map((p) => ({ ...p, tracking: detail.tracking, customerName: detail.customerName, status: detail.status })),
      itemPhotos: detail.photos,
      index,
      baseName: detail.tracking,
    });
  };

  const openFlatLightbox = async (photo, index, dayPhotos) => {
    // Browsing context = the photos of that day; download-all = the whole package.
    const res = await getGalleryItemPhotos("packages", photo.packageId);
    setLightbox({
      photos: dayPhotos,
      itemPhotos: res.success ? res.data.photos : null,
      index,
      baseName: photo.tracking,
    });
  };

  const categories = [
    { value: "package_photo", label: t("gallery.category.package_photo") },
    { value: "courier_label", label: t("gallery.category.courier_label") },
    { value: "delivery_proof", label: t("gallery.category.delivery_proof") },
    { value: "recipient_photo", label: t("gallery.category.recipient_photo") },
    { value: "cargo_items", label: t("gallery.category.cargo_items") },
    { value: "cargo_box", label: t("gallery.category.cargo_box") },
    { value: "cancel_evidence", label: t("gallery.category.cancel_evidence") },
  ];

  const sections = groupByDay(rows, (r) => (grouped ? r.latestPhotoAt : r.timestamp));
  const canLoadMore = rows.length < total;

  return (
    <Box>
      <FilterBar
        filters={filters}
        onChange={setFilters}
        categories={categories}
        showGroupToggle
        grouped={grouped}
        onGroupedChange={(g) => {
          // Clear immediately — grouped rows and flat photo rows have different
          // shapes, and stale rows would render wrong until the reload lands.
          setGrouped(g);
          setRows([]);
          setTotal(0);
        }}
        viewMode={!grouped ? viewMode : undefined}
        onViewModeChange={!grouped ? setViewMode : undefined}
        searchPlaceholder={t("gallery.searchPackages")}
      />

      <Typography sx={{ fontSize: 12.5, color: TEXT_SECONDARY, mb: 0.5 }}>
        {grouped
          ? t("gallery.resultPackages", { count: total })
          : t("gallery.resultPhotos", { count: total })}
      </Typography>

      {loading && rows.length === 0 && (
        <Box sx={!grouped && viewMode === "list" ? LIST_SX : grouped ? CARD_GRID_SX : GRID_SX}>
          {Array.from({ length: 12 }).map((_, i) =>
            !grouped && viewMode === "list" ? <RowSkeleton key={i} /> : <TileSkeleton key={i} />
          )}
        </Box>
      )}

      {!loading && rows.length === 0 && (
        <EmptyHint title={t("gallery.emptyTitle")} hint={t("gallery.emptyHint")} />
      )}

      {sections.map((sec) => (
        <Box key={sec.key}>
          <DayHeader
            date={sec.date}
            count={grouped ? sec.items.reduce((s, r) => s + r.photoCount, 0) : sec.items.length}
            todayLabel={t("gallery.today")}
            photosLabel={t("gallery.photos")}
            lang={i18n.language}
          />
          <Box sx={grouped ? CARD_GRID_SX : viewMode === "list" ? LIST_SX : GRID_SX}>
            {grouped
              ? sec.items.map((row) => (
                  <PackageCard key={row.packageId} row={row} t={t} onOpen={() => setDrawerPkg(row.packageId)} />
                ))
              : sec.items.map((p, i) =>
                  viewMode === "list" ? (
                    <PhotoListRow
                      key={`${p.packageId}-${i}`}
                      album="packages"
                      photo={p}
                      title={p.tracking}
                      subtitle={p.customerName || ""}
                      badge={p.category ? t(`gallery.category.${p.category}`) : null}
                      stationBadge={p.stationName || null}
                      timestamp={p.timestamp}
                      onClick={() => openFlatLightbox(p, sec.items.indexOf(p), sec.items)}
                    />
                  ) : (
                    <PhotoTile
                      key={`${p.packageId}-${i}`}
                      album="packages"
                      photo={p}
                      title={p.tracking}
                      subtitle={`${p.customerName || ""} · ${fmtTime(p.timestamp)}`}
                      badge={p.category ? t(`gallery.category.${p.category}`) : null}
                      stationBadge={p.stationName || null}
                      onClick={() => openFlatLightbox(p, sec.items.indexOf(p), sec.items)}
                    />
                  )
                )}
          </Box>
        </Box>
      ))}

      {canLoadMore && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Button variant="outlined" color="secondary" disabled={loading} onClick={() => load(page + 1, true)}>
            {loading ? t("common.loading") : t("gallery.loadMore")}
          </Button>
        </Box>
      )}

      <PackageDrawer
        open={!!drawerPkg}
        onClose={() => setDrawerPkg(null)}
        packageId={drawerPkg}
        t={t}
        i18n={i18n}
        onOpenLightbox={openPkgLightbox}
        onOpenTracking={setTrackingDialog}
      />

      {lightbox && (
        <PhotoLightbox
          open
          onClose={() => setLightbox(null)}
          album="packages"
          photos={lightbox.photos}
          index={lightbox.index}
          onIndexChange={(i) => setLightbox((lb) => ({ ...lb, index: i }))}
          itemPhotos={lightbox.itemPhotos}
          downloadBaseName={(p) => p.tracking || lightbox.baseName}
          meta={(p) => [
            {
              k: t("gallery.trackingNo"),
              v: p.tracking,
              onClick: p.tracking ? () => setTrackingDialog(p.tracking) : undefined,
            },
            { k: t("gallery.customer"), v: p.customerName },
            { k: t("gallery.category.label"), v: p.category ? t(`gallery.category.${p.category}`) : null },
            { k: t("gallery.station"), v: p.stationName },
          ]}
        />
      )}

      <TrackingDetailDialog tracking={trackingDialog} onClose={() => setTrackingDialog(null)} />
    </Box>
  );
};

export default PackagesTab;
